// Saved browser-agent "skills" / extraction templates — per browser, localStorage.
// Click a skill in the sidebar to run it in the current session.
// A skill may be a plain task (kind:"task") OR a schema-first extraction template
// (kind:"extract") that carries the fields to pull, output format, and a pagination bound.
// Each skill may also be associated with a site (the site the agent starts on).

export type FieldType = 'string' | 'number' | 'date' | 'boolean';

export interface FieldDef {
  name: string;
  type: FieldType;
  required: boolean;
  description?: string;
}

export interface AgentSkill {
  id: string;
  name: string;
  prompt: string;
  siteId?: string;          // associated Browser Agent site (see sitesStore)
  kind?: 'task' | 'extract';// "extract" => schema-first extraction mode; default "task"
  schema?: FieldDef[];      // fields to pull (only meaningful when kind === "extract")
  output?: 'table' | 'json';// how to render results; default "table"
  maxPages?: number;        // pagination bound for extract mode
}

// Alias for clarity at call sites that think in "templates".
export type ExtractionTemplate = AgentSkill;

const KEY = 'ba_skills_v2';
const LEGACY_KEY = 'ba_skills_v1';

const DEFAULTS: AgentSkill[] = [
  { id: 'summarize', name: 'Summarize this page', prompt: 'Read the current page and give a concise summary of the key information, then finish.', kind: 'task' },
  { id: 'extract-json', name: 'Extract data as JSON', prompt: 'Extract the main structured data shown on this page and return it as JSON, then finish.', kind: 'task' },
  { id: 'open-pricing', name: 'Find & open Pricing', prompt: 'Find the Pricing or Plans page, open it, and list the plan names and prices. Return them as JSON.', kind: 'task' },
];

function notify(): void {
  try { window.dispatchEvent(new CustomEvent('ba:skills-changed')); } catch { /* ignore */ }
}

function normalize(s: any): AgentSkill {
  return {
    id: s.id,
    name: s.name,
    prompt: s.prompt,
    siteId: s.siteId,
    kind: s.kind === 'extract' ? 'extract' : 'task',
    schema: Array.isArray(s.schema) ? s.schema : undefined,
    output: s.output === 'json' ? 'json' : (s.kind === 'extract' ? 'table' : undefined),
    maxPages: typeof s.maxPages === 'number' ? s.maxPages : undefined,
  };
}

function read(): AgentSkill[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) {
      // Migrate v1 skills verbatim (as plain tasks) if present, else seed defaults.
      let seed = DEFAULTS;
      try {
        const legacy = localStorage.getItem(LEGACY_KEY);
        if (legacy) {
          const arr = JSON.parse(legacy);
          if (Array.isArray(arr) && arr.length) seed = arr.map(normalize);
        }
      } catch { /* ignore */ }
      localStorage.setItem(KEY, JSON.stringify(seed));
      return seed;
    }
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.map(normalize) : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

function write(skills: AgentSkill[]): void {
  try { localStorage.setItem(KEY, JSON.stringify(skills)); } catch { /* ignore */ }
  notify();
}

function newId(): string {
  return `skill-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

export function listSkills(): AgentSkill[] {
  return read();
}

export function getSkill(id: string): AgentSkill | undefined {
  return read().find((s) => s.id === id);
}

type SkillInput = {
  id?: string; name: string; prompt: string; siteId?: string;
  kind?: 'task' | 'extract'; schema?: FieldDef[]; output?: 'table' | 'json'; maxPages?: number;
};

export function saveSkill(skill: SkillInput): AgentSkill {
  const skills = read();
  const fields: Partial<AgentSkill> = {
    name: skill.name,
    prompt: skill.prompt,
    siteId: skill.siteId,
    kind: skill.kind === 'extract' ? 'extract' : 'task',
    schema: skill.kind === 'extract' ? (skill.schema ?? []) : undefined,
    output: skill.kind === 'extract' ? (skill.output ?? 'table') : undefined,
    maxPages: skill.kind === 'extract' ? skill.maxPages : undefined,
  };
  if (skill.id) {
    const next = skills.map((s) => (s.id === skill.id ? { ...s, ...fields } : s));
    write(next);
    return next.find((s) => s.id === skill.id)!;
  }
  const created: AgentSkill = { id: newId(), name: skill.name, prompt: skill.prompt, ...fields } as AgentSkill;
  write([...skills, created]);
  return created;
}

export function deleteSkill(id: string): void {
  write(read().filter((s) => s.id !== id));
}

/**
 * Import skills from an uploaded file's text. Accepts:
 *  - JSON object `{ name, prompt, siteId?, kind?, schema?, output?, maxPages? }`
 *  - JSON array of such objects, or `{ skills: [...] }`
 *  - Plain text / markdown → one task skill (caller supplies a fallback name)
 * Returns the created skills. Throws on unusable input.
 */
export function importSkillsFromText(text: string, fallbackName: string): AgentSkill[] {
  const trimmed = text.trim();
  let items: Array<Partial<SkillInput>> = [];

  let parsed: any = null;
  try { parsed = JSON.parse(trimmed); } catch { parsed = null; }

  if (parsed && typeof parsed === 'object') {
    if (Array.isArray(parsed)) items = parsed;
    else if (Array.isArray(parsed.skills)) items = parsed.skills;
    else items = [parsed];
  } else {
    if (!trimmed) throw new Error('File is empty.');
    items = [{ name: fallbackName, prompt: trimmed }];
  }

  const created: AgentSkill[] = [];
  for (const it of items) {
    const prompt = (it.prompt ?? '').toString().trim();
    const isExtract = it.kind === 'extract';
    // An extract template is valid with a schema even if the prompt is light.
    if (!prompt && !(isExtract && Array.isArray(it.schema) && it.schema.length)) continue;
    const name = (it.name ?? fallbackName).toString().trim() || fallbackName;
    created.push(saveSkill({
      name,
      prompt,
      siteId: it.siteId,
      kind: isExtract ? 'extract' : 'task',
      schema: it.schema as FieldDef[] | undefined,
      output: it.output,
      maxPages: it.maxPages,
    }));
  }
  if (created.length === 0) throw new Error('No usable skill was found in the file.');
  return created;
}
